/* .btn family from the storyboard helmet. Every size keeps a 44 px minimum target. */
.btn {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 8px;
  /* QA-037 (APP-067 §10): a label never paints outside its button. A button is as wide as its
     words, never wider than its container, and never squeezed by its neighbours; only a box held
     narrower than its words (a full-width button in a narrow column) wraps them, centred and
     balanced, inside the same 44 px minimum. In a table cell it keeps one line, so columns size
     as before. Padding is unchanged, so no button that fits grows. */
  max-width: 100%;
  min-height: var(--control-height);
  padding: 0 18px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: var(--fw-regular);
  letter-spacing: 0.08em;
  line-height: 1.1;
  text-align: center;
  text-transform: uppercase;
  text-decoration: none;
  /* The style only: `text-wrap` would also set the mode and stop a one-line context's nowrap. */
  text-wrap-style: balance;
  overflow-wrap: break-word;
  cursor: pointer;
  transition:
    background-color var(--dur-fast) ease,
    border-color var(--dur-fast) ease,
    color var(--dur-fast) ease;
}

.btn[data-disabled] {
  cursor: not-allowed;
  opacity: 0.55;
}

.sm {
  padding: 0 12px;
  font-size: 12px;
}

:global(td) .btn,
:global(th) .btn {
  white-space: nowrap;
}

.lg {
  min-height: 52px;
  font-size: 14px;
}

.block {
  width: 100%;
}

/* Gold: celebration actions (employee, letters released). */
.gold {
  background: var(--brand-gold);
  color: var(--brand-navy-950);
}

.gold:hover,
.gold[data-hovered] {
  background: var(--brand-gold-300);
  color: var(--brand-navy-950);
}

/* Cyan: work actions, with navy text. */
.cyan {
  background: var(--brand-cyan);
  color: var(--brand-navy-950);
}

.cyan:hover,
.cyan[data-hovered] {
  background: var(--brand-cyan-400);
  color: var(--brand-navy-950);
}

.navy {
  background: var(--brand-navy);
  color: var(--white);
}

.navy:hover,
.navy[data-hovered] {
  background: var(--brand-navy-700);
  color: var(--white);
}

/* Ghost on light grounds (.btn-o). */
.ghost {
  background: var(--white);
  color: var(--brand-navy);
  border-color: var(--border-control);
}

.ghost:hover,
.ghost[data-hovered] {
  border-color: var(--brand-navy);
  color: var(--brand-navy);
}

/* Ghost on dark grounds (.btn-gw). */
.ghostOnDark {
  background: transparent;
  color: var(--white);
  border-color: var(--white-a45);
}

.ghostOnDark:hover,
.ghostOnDark[data-hovered] {
  background: var(--white-a10);
  color: var(--white);
}

/* A button that reads as a link inside running text ("History"). Its box is a 44 px target
   (QA-043) while the line keeps its look: the box grows around the word, centred on it, and
   negative margins give the extra height back, so the line's height and the words around it don't
   move. The underline and the focus ring stay on the word. */
.inlineLink {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 44px;
  min-height: 44px;
  margin-block: -12px;
  margin-block: calc((1lh - 44px) / 2);
  padding: 0 1px;
  border: 0;
  background: none;
  color: var(--text-link);
  font: inherit;
  vertical-align: baseline;
  cursor: pointer;
}

.inlineText {
  text-decoration: underline;
}

.inlineLink:hover,
.inlineLink[data-hovered] {
  color: var(--text-link-hover-on-light);
}

.inlineLink[data-focus-visible] {
  outline: none;
}

.inlineLink[data-focus-visible] .inlineText {
  border-radius: 2px;
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}
